import React, { useState, useEffect, useMemo } from "react";
import { PortfoliosAPI, type PortfolioFieldData, type FieldOverride } from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import { useTaskProgressDialog } from "@/context/TaskProgressDialogContext";
import { format } from "date-fns";
import {
  Dialog,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import FormInput from "../ui/form-input";
import { FieldSection } from "@/components/ui/FieldSection";
import Drawer from "@/components/ui/drawer";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import { BenchmarkProxySelector } from "@/components/ui/BenchmarkProxySelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faTrashAlt,
  faSquareBolt,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

interface PortfolioFieldManagementProps {
  portfolioId: string;
  onUpdate?: () => void;
}

// Inline SVG icons
function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

// Field editor modal component
function FieldEditor({
  fieldName,
  fieldPath,
  currentValue,
  overrides,
  options,
  fieldType,
  onSave,
  onClose,
  onDelete,
  deletingOverride,
}: {
  fieldName: string;
  fieldPath: string;
  currentValue: any;
  overrides: FieldOverride[];
  options?: string[];
  fieldType?: "text" | "number" | "dropdown" | "exposure";
  onSave: (value: any, validFrom?: string, validTo?: string) => void;
  onClose: () => void;
  onDelete: (overrideId: string) => void;
  deletingOverride?: string | null;
}) {
  const [value, setValue] = useState<any>("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [advancedMode, setAdvancedMode] = useState(false);
  const [longValues, setLongValues] = useState<Record<string, number>>({});
  const [shortValues, setShortValues] = useState<Record<string, number>>({});

  // Format display value based on type
  const formatValue = (val: any) => {
    if (val === null || val === undefined) return "Not set";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    if (typeof val === "number") return val.toString();
    if (typeof val === "object") {
      if (val.primary !== undefined) return val.primary;
      if (val.long) {
        const entries = Object.entries(val.long);
        if (entries.length === 1 && entries[0][1] === 1) return entries[0][0];
        return "Multiple values";
      }
      return JSON.stringify(val);
    }
    return String(val);
  };

  // Get the appropriate input type
  const getInputType = () => {
    if (typeof currentValue === "boolean") return "checkbox";
    if (typeof currentValue === "number") return "number";
    if (currentValue instanceof Date) return "date";
    if (options && options.length > 0) return "select";
    return "text";
  };

  const handleSave = () => {
    let finalValue: any = value;

    if (fieldType === "exposure" && !advancedMode && value) {
      // Simple mode - create exposure structure
      finalValue = {
        long: { [value]: 1.0 },
        short: {},
        net: { [value]: 1.0 },
        primary: value,
      };
    } else if (fieldType === "exposure" && advancedMode) {
      // Advanced mode - build from longValues and shortValues
      const long: Record<string, number> = {};
      const short: Record<string, number> = {};
      const net: Record<string, number> = {};
      let maxNetVal = 0;
      let primary = "";

      // Process long values
      Object.entries(longValues).forEach(([key, val]) => {
        if (val > 0) {
          long[key] = val / 100; // Convert percentage to decimal
        }
      });

      // Process short values
      Object.entries(shortValues).forEach(([key, val]) => {
        if (val > 0) {
          short[key] = val / 100; // Convert percentage to decimal
        }
      });

      // Calculate net values
      const allKeys = new Set([...Object.keys(long), ...Object.keys(short)]);
      allKeys.forEach((key) => {
        const longVal = long[key] || 0;
        const shortVal = short[key] || 0;
        const netVal = longVal - shortVal;
        if (netVal !== 0) {
          net[key] = netVal;
          if (Math.abs(netVal) > Math.abs(maxNetVal)) {
            maxNetVal = netVal;
            primary = key;
          }
        }
      });

      finalValue = { long, short, net, primary };
    } else if (getInputType() === "checkbox") {
      finalValue = value === "true";
    } else if (getInputType() === "number") {
      finalValue = parseFloat(value);
    }

    onSave(finalValue, validFrom || undefined, validTo || undefined);
  };

  return (
    <Drawer isOpen={true} onClose={onClose} title={fieldName} width="max-w-2xl">
      <div className="p-6 space-y-6 text-white">
        {/* Current value */}
        <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
          <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
            Current Value
          </div>
          <div className="font-medium">{formatValue(currentValue)}</div>
        </div>

        {/* Existing overrides */}

        {/* New override form */}
        <div className="space-y-6">
          <h4 className="text-lg font-medium">Create new override...</h4>

          {/* Value input */}
          {fieldType === "exposure" && (
            <div className="mb-4">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={advancedMode}
                  onChange={(e) => setAdvancedMode(e.target.checked)}
                  className="mr-2"
                />
                Advanced Mode (Long/Short Entry)
              </label>
            </div>
          )}

          {fieldType === "exposure" && !advancedMode && options ? (
            <div>
              <label className="block text-sm font-medium mb-2">Value</label>
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700"
              >
                <option value="">Select {fieldName}</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          ) : fieldType === "exposure" && advancedMode && options ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm font-medium">
                <div>Exposure</div>
                <div className="text-center">Long %</div>
                <div className="text-center">Short %</div>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {options.map((opt) => (
                  <div
                    key={opt}
                    className="grid grid-cols-3 gap-4 items-center"
                  >
                    <label className="text-sm">{opt}</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={longValues[opt] || ""}
                      onChange={(e) =>
                        setLongValues((prev) => ({
                          ...prev,
                          [opt]: parseFloat(e.target.value) || 0,
                        }))
                      }
                      placeholder="0"
                      className="w-full px-2 py-1 border rounded dark:bg-slate-700 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={shortValues[opt] || ""}
                      onChange={(e) =>
                        setShortValues((prev) => ({
                          ...prev,
                          [opt]: parseFloat(e.target.value) || 0,
                        }))
                      }
                      placeholder="0"
                      className="w-full px-2 py-1 border rounded dark:bg-slate-700 text-center"
                    />
                  </div>
                ))}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                Long Total:{" "}
                {Object.values(longValues)
                  .reduce((a, b) => a + b, 0)
                  .toFixed(4)}
                % | Short Total:{" "}
                {Object.values(shortValues)
                  .reduce((a, b) => a + b, 0)
                  .toFixed(4)}
                %
              </div>
            </div>
          ) : getInputType() === "select" && options ? (
            <div>
              <label className="block text-sm font-medium mb-2">Value</label>
              <select
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700"
              >
                <option value="">Select value</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          ) : getInputType() === "checkbox" ? (
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={value === "true"}
                  onChange={(e) =>
                    setValue(e.target.checked ? "true" : "false")
                  }
                  className="mr-2"
                />
                Enable
              </label>
            </div>
          ) : (
            <div>
              <FormInput
                id="value"
                label="Value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter value"
              />
            </div>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FormInput
                id="validFrom"
                label="Valid From"
                optional
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                placeholder="From beginning"
              />
            </div>
            <div>
              <FormInput
                id="validTo"
                label="Valid To"
                optional
                type="date"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
                placeholder="Ongoing"
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <Button onClick={onClose} plain>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                !value &&
                (!advancedMode ||
                  (Object.keys(longValues).length === 0 &&
                    Object.keys(shortValues).length === 0))
              }
              color="indigo"
            >
              Save Override
            </Button>
          </div>
        </div>
        {overrides.length > 0 && (
          <div>
            <h4 className="font-medium text-lg mb-3">Override History</h4>
            <div className="space-y-3">
              {overrides.map((override) => (
                <div
                  key={override.client_portfolio_data_id}
                  className="flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium">
                      {formatValue(override.value)}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {override.valid_from
                        ? format(new Date(override.valid_from), "MMM d, yyyy")
                        : "From beginning"}
                      {" → "}
                      {override.valid_to
                        ? format(new Date(override.valid_to), "MMM d, yyyy")
                        : "Ongoing"}
                    </div>
                    <div className="text-xs text-slate-500">
                      By {override.updated_by} on{" "}
                      {format(
                        new Date(override.updated_at || new Date()),
                        "MMM d, yyyy"
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(override.client_portfolio_data_id);
                    }}
                    disabled={
                      deletingOverride === override.client_portfolio_data_id
                    }
                    className="text-red-400 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed p-2"
                  >
                    {deletingOverride === override.client_portfolio_data_id ? (
                      <div className="flex items-center space-x-1">
                        <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full"></div>
                        <span className="text-xs">Recalculating...</span>
                      </div>
                    ) : (
                      <FontAwesomeIcon icon={faTrashAlt} className="size-6" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}

export const PortfolioFieldManagement: React.FC<
  PortfolioFieldManagementProps
> = ({ portfolioId, onUpdate }) => {
  const [fieldData, setFieldData] = useState<PortfolioFieldData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{
    name: string;
    path: string;
    data: any;
    type: "text" | "number" | "dropdown" | "exposure";
  } | null>(null);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    favourite: true,
    fundamental: false,
    exposures: false,
  });
  const [favouriteFields, setFavouriteFields] = useState<string[]>([]);
  const [deletingOverride, setDeletingOverride] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAuth();

  const { showSuccess, showInfo } = useNotification();

  useEffect(() => {
    if (portfolioId) {
      loadFieldData();
      loadFavouriteFields();
    }
  }, [portfolioId]);

  const loadFieldData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await PortfoliosAPI.getPortfolioFields(portfolioId);
      setFieldData(data);
      return data; // Return the data so it can be used immediately
    } catch (error) {
      console.error("Error loading portfolio fields:", error);
      setError("Failed to load portfolio fields");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadFavouriteFields = async () => {
    try {
      // TODO: Load from company preferences when available
      // For now, leave empty as requested
      setFavouriteFields([]);
    } catch (error) {
      console.error("Error loading favourite fields:", error);
    }
  };

  const { openRefreshPreview } = useTaskProgressDialog();

  const handleSaveOverride = async (
    value: any,
    validFrom?: string,
    validTo?: string
  ) => {
    if (!editingField) return;

    try {
      await PortfoliosAPI.createPortfolioFieldOverride({
        portfolio_id: portfolioId,
        field_path: editingField.path,
        value,
        valid_from: validFrom,
        valid_to: validTo,
        skip_refresh: true,
      });

      openRefreshPreview({ resource_ids: [portfolioId], title: "Refreshing snapshots" });

      await loadFieldData();
      if (onUpdate) onUpdate();
      setEditingField(null);
    } catch (error) {
      console.error("Error creating override:", error);
      setError("Failed to create field override");
    }
  };

  const handleDeleteOverride = async (overrideId: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this override? This will recalculate the portfolio snapshot."
      )
    )
      return;

    try {
      setDeletingOverride(overrideId);
      setError("Recalculating portfolio snapshot...");

      await PortfoliosAPI.deletePortfolioFieldOverride(overrideId);

      // Close the modal first
      setEditingField(null);

      // Then refresh the field data
      await loadFieldData();
      if (onUpdate) onUpdate();

      setError(null);
    } catch (error) {
      console.error("Error deleting override:", error);
      setError("Failed to delete override");
    } finally {
      setDeletingOverride(null);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Filter and sort fields based on search query
  const filteredAndSortedData = useMemo(() => {
    if (!fieldData) return null;

    const filterAndSort = (fields: Record<string, any>) => {
      return Object.entries(fields)
        .filter(
          ([fieldName]) =>
            fieldName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            formatFieldName(fieldName)
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
        )
        .sort(([a], [b]) => a.localeCompare(b));
    };

    const filterFavouriteFields = (fieldPaths: string[]) => {
      return fieldPaths
        .filter((fieldPath) => {
          const fieldName = fieldPath.split(".").pop() || "";
          return (
            fieldName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            formatFieldName(fieldName)
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          );
        })
        .sort();
    };

    return {
      ...fieldData,
      fundamental_data: Object.fromEntries(
        filterAndSort(fieldData.fundamental_data)
      ),
      exposures: Object.fromEntries(filterAndSort(fieldData.exposures)),
      favouriteFields: filterFavouriteFields(favouriteFields),
    };
  }, [fieldData, favouriteFields, searchQuery]);

  const renderFieldValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined)
      return <span className="text-slate-400">Not set</span>;
    if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
    if (typeof value === "number") {
      return <span>{value.toFixed(4)}</span>;
    }
    if (typeof value === "object") {
      if (value.primary !== undefined) {
        return <span>{value.primary}</span>;
      }
      if (value.long) {
        const entries = Object.entries(value.long);
        if (entries.length === 1 && entries[0][1] === 1) {
          return <span>{entries[0][0]}</span>;
        }
        return <span>Multiple values</span>;
      }
      return <span>Complex value</span>;
    }
    return <span>{String(value)}</span>;
  };

  const renderField = (
    fieldName: string,
    fieldPath: string,
    fieldInfo: any,
    fieldType: "text" | "number" | "dropdown" | "exposure" = "text"
  ) => {
    const hasOverrides = fieldInfo.overrides && fieldInfo.overrides.length > 0;
    const activeOverride = fieldInfo.overrides?.find((o: any) => {
      const now = new Date();
      const from = o.valid_from ? new Date(o.valid_from) : new Date(0);
      const to = o.valid_to ? new Date(o.valid_to) : new Date("9999-12-31");
      return now >= from && now <= to;
    });
    const isCustomized = fieldInfo.is_customized;

    return (
      <div
        key={fieldPath}
        className="flex items-center justify-between py-3 px-4 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer"
        onClick={() =>
          setEditingField({
            name: formatFieldName(fieldName),
            path: fieldPath,
            data: fieldInfo,
            type: fieldType,
          })
        }
      >
        <div className="flex flex-none flex-col">
          <div className="flex gap-2 items-center">
            {formatFieldName(fieldName)}{" "}
            {isCustomized && (
              <Tooltip
                content={
                  activeOverride ? (
                    <div className="text-sm">
                      <div className="font-medium mb-1">Field Override</div>
                      <div className="font-mono text-gray-400">
                        <div>Modified by {activeOverride.updated_by}</div>
                        <div>
                          on{" "}
                          {format(
                            new Date(activeOverride.updated_at || new Date()),
                            "MMM d, yyyy"
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm">
                      <div className="font-medium">Customized Field</div>
                      <div>This field has been modified</div>
                    </div>
                  )
                }
                placement="top"
              >
                <FontAwesomeIcon
                  icon={faSquareBolt}
                  className="text-lg text-yellow-400 cursor-help"
                />
              </Tooltip>
            )}
          </div>
          <div className="text-slate-600 dark:text-slate-400">
            {renderFieldValue(fieldInfo.current_value)}
          </div>
        </div>
        <button className="p-2 text-slate-600 hover:text-slate-900 dark:text-slate-500 dark:hover:text-slate-100">
          <FontAwesomeIcon icon={faPenToSquare} className="size-5" />
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading portfolio fields...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!filteredAndSortedData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No field data available</div>
      </div>
    );
  }

  // Helper function to save benchmark overrides
  const handleBenchmarkSave = async (field: 'primary' | 'proxy', benchmarkObject: any) => {
    // For portfolios, only support primary benchmark
    if (field !== 'primary') {
      setError('Portfolios only support primary benchmarks');
      return;
    }

    const fieldPath = `benchmark.${field}`;
    try {
      await PortfoliosAPI.createPortfolioFieldOverride({
        portfolio_id: portfolioId,
        field_path: fieldPath,
        value: benchmarkObject,
        skip_refresh: true,
      });

      openRefreshPreview({ resource_ids: [portfolioId], title: "Refreshing snapshots" });

      await loadFieldData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error creating benchmark override:', error);
      setError('Failed to save benchmark selection');
    }
  };

  // Extract current benchmark values from field data
  const getCurrentBenchmarkValues = () => {
    if (!fieldData) return { primary: undefined, proxy: undefined };

    const benchmarkData = (fieldData as any).benchmark || {};

    return {
      primary: benchmarkData.primary?.current_value,
      proxy: undefined // Portfolios don't use proxy benchmarks
    };
  };

  const currentBenchmarks = getCurrentBenchmarkValues();

  return (
    <div className=" space-y-6  h-full text-white">
      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-5 w-5 text-slate-400" />
        </div>

        <FormInput
          id="search"
          value={searchQuery}
          icon={<SearchIcon className="h-5 w-5 text-slate-400" />}
          placeholder="Search fields..."
          onChange={(e) => setSearchQuery(e.target.value)}
          iconPosition="start"
          onClear={() => setSearchQuery("")}
          canClear
          showClearButton
        />
      </div>

      {/* Benchmark Selector */}
      <BenchmarkProxySelector
        currentPrimary={currentBenchmarks.primary}
        currentProxy={undefined}
        onSave={handleBenchmarkSave}
        type="portfolio"
      />

      {/* Favourite Fields */}
      {filteredAndSortedData.favouriteFields.length > 0 && (
        <FieldSection
          title="Favourite Fields"
          description="Organization-specific favourite fields"
          icon={<StarIcon className="h-4 w-4 text-yellow-500" />}
          isExpanded={expandedSections.favourite}
          onToggle={() => toggleSection("favourite")}
        >
          {filteredAndSortedData.favouriteFields.map((fieldPath) => {
            const [section, ...pathParts] = fieldPath.split(".");
            const fieldName = pathParts.join(".");

            if (
              section === "fundamental_data" &&
              filteredAndSortedData.fundamental_data[fieldName]
            ) {
              return renderField(
                fieldName,
                fieldPath,
                filteredAndSortedData.fundamental_data[fieldName]
              );
            } else if (
              section === "exposures" &&
              filteredAndSortedData.exposures[fieldName]
            ) {
              return renderField(
                fieldName,
                fieldPath,
                filteredAndSortedData.exposures[fieldName],
                "exposure"
              );
            }
            return null;
          })}
        </FieldSection>
      )}

      {/* Fundamental Data */}
      {Object.keys(filteredAndSortedData.fundamental_data).length > 0 && (
        <FieldSection
          title="Fundamental Data"
          description="Core portfolio information and metrics"
          isExpanded={expandedSections.fundamental}
          onToggle={() => toggleSection("fundamental")}
        >
          {Object.entries(filteredAndSortedData.fundamental_data).map(
            ([fieldName, fieldInfo]: [string, any]) =>
              renderField(
                fieldName,
                `fundamental_data.${fieldName}`,
                fieldInfo,
                fieldInfo.field_type === "dropdown" ? "dropdown" :
                fieldInfo.field_type === "number" || fieldInfo.field_type === "float" || fieldInfo.field_type === "int" ? "number" :
                typeof fieldInfo.current_value === "number" ? "number" : "text"
              )
          )}
        </FieldSection>
      )}

      {/* Exposures */}
      {Object.keys(filteredAndSortedData.exposures).length > 0 && (
        <FieldSection
          title="Exposures"
          description="Asset allocation, geographic, and sector exposures"
          isExpanded={expandedSections.exposures}
          onToggle={() => toggleSection("exposures")}
        >
          {Object.entries(filteredAndSortedData.exposures).map(
            ([fieldName, fieldInfo]) =>
              renderField(
                fieldName,
                `exposures.${fieldName}`,
                fieldInfo,
                "exposure"
              )
          )}
        </FieldSection>
      )}

      {/* No results message */}
      {searchQuery &&
        Object.keys(filteredAndSortedData.fundamental_data).length === 0 &&
        Object.keys(filteredAndSortedData.exposures).length === 0 &&
        filteredAndSortedData.favouriteFields.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            No fields found matching "{searchQuery}"
          </div>
        )}

      {/* Field Editor Modal */}
      {editingField && (
        <FieldEditor
          fieldName={editingField.name}
          fieldPath={editingField.path}
          currentValue={editingField.data.current_value}
          overrides={editingField.data.overrides || []}
          options={editingField.data.options}
          fieldType={editingField.type}
          onSave={handleSaveOverride}
          onClose={() => setEditingField(null)}
          onDelete={handleDeleteOverride}
          deletingOverride={deletingOverride}
        />
      )}
    </div>
  );
};
